// Call quality from RTCPeerConnection.getStats(), same numbers as the test page
// (mb-addon/assets/siptest.html): packets per second, loss, jitter, RTT and
// which network path the audio takes.

export type PathKind = 'direct' | 'relay-udp' | 'relay-tcp' | 'relay-tls' | 'unknown';
export type QualityLevel = 'good' | 'fair' | 'poor';

/** One reading of the counters (cumulative values, as the browser reports them). */
export interface StatsSample {
  at: number; // ms
  received: number;
  sent: number;
  lost: number;
  jitterMs: number | null;
  rttMs: number | null;
  path: PathKind;
}

export interface Quality {
  level: QualityLevel;
  lossPct: number; // over the last few seconds
  jitterMs: number | null;
  rttMs: number | null;
  inPerSec: number;
  outPerSec: number;
  path: PathKind;
  noAudio: boolean; // nothing received lately
}

const WINDOW = 4; // samples (~8 s at one per 2 s)

/** Turn the last few samples into what the call screen shows. Needs at least 2 samples. */
export function computeQuality(samples: readonly StatsSample[]): Quality | null {
  if (samples.length < 2) return null;
  const recent = samples.slice(-WINDOW);
  const first = recent[0]!;
  const last = recent[recent.length - 1]!;
  const prev = recent[recent.length - 2]!;
  const secs = Math.max((last.at - first.at) / 1000, 0.001);
  const lastSecs = Math.max((last.at - prev.at) / 1000, 0.001);

  const got = Math.max(last.received - first.received, 0);
  const lost = Math.max(last.lost - first.lost, 0);
  const lossPct = got + lost > 0 ? (lost / (got + lost)) * 100 : 0;
  const inPerSec = Math.max(last.received - prev.received, 0) / lastSecs;
  const outPerSec = Math.max(last.sent - prev.sent, 0) / lastSecs;
  const noAudio = got === 0 && secs >= 2;

  let level: QualityLevel = 'good';
  const rtt = last.rttMs ?? 0;
  const jitter = last.jitterMs ?? 0;
  if (lossPct > 2 || jitter > 30 || rtt > 300) level = 'fair';
  if (lossPct > 5 || jitter > 60 || rtt > 500 || noAudio) level = 'poor';

  return {
    level,
    lossPct: Math.round(lossPct * 10) / 10,
    jitterMs: last.jitterMs,
    rttMs: last.rttMs,
    inPerSec: Math.round(inPerSec),
    outPerSec: Math.round(outPerSec),
    path: last.path,
    noAudio,
  };
}

/**
 * The warning the call screen shows, or null when the call looks fine.
 * `relayed`: the call uses the relay on purpose (the Settings switch, or a
 * recovery after a network drop), so a relay path isn't the network's fault.
 */
export function qualityAdvice(q: Quality | null, relayed = false): string | null {
  if (!q) return null;
  if (q.noAudio) return 'No audio is arriving. Check your internet connection.';
  if (q.lossPct > 5) {
    return 'Your network is dropping audio. If you use a VPN, let CloudPhone bypass it (split tunnelling), or try another network.';
  }
  if (q.path === 'relay-tls' || q.path === 'relay-tcp') {
    if (q.rttMs === null || q.rttMs <= 400) return null;
    return relayed
      ? 'Audio may lag on this connection. Another network usually helps.'
      : 'Your network blocks the fast call path, so audio may lag. Another network or turning off the VPN usually helps.';
  }
  return null;
}

export function pathLabel(p: PathKind): string {
  switch (p) {
    case 'direct':
      return 'Direct';
    case 'relay-udp':
      return 'Relay (UDP)';
    case 'relay-tcp':
      return 'Relay (TCP)';
    case 'relay-tls':
      return 'Relay (TLS)';
    default:
      return 'Connecting';
  }
}

interface StatLike {
  id: string;
  type: string;
  [k: string]: unknown;
}

/** Read one sample from a live peer connection. */
export async function readSample(pc: RTCPeerConnection): Promise<StatsSample | null> {
  let report: RTCStatsReport;
  try {
    report = await pc.getStats();
  } catch {
    return null;
  }
  const byId = new Map<string, StatLike>();
  report.forEach((r: StatLike) => byId.set(r.id, r));

  let pair: StatLike | undefined;
  let inbound: StatLike | undefined;
  let outbound: StatLike | undefined;
  let remoteIn: StatLike | undefined;
  for (const r of byId.values()) {
    if (r.type === 'transport' && typeof r.selectedCandidatePairId === 'string') pair = byId.get(r.selectedCandidatePairId);
    const audio = r.kind === 'audio' || r.mediaType === 'audio';
    if (r.type === 'inbound-rtp' && audio) inbound = r;
    if (r.type === 'outbound-rtp' && audio) outbound = r;
    if (r.type === 'remote-inbound-rtp') remoteIn = r;
  }
  if (!pair) {
    for (const r of byId.values()) {
      if (r.type === 'candidate-pair' && (r.selected === true || (r.nominated === true && r.state === 'succeeded'))) pair = r;
    }
  }

  const local = pair ? byId.get(String(pair.localCandidateId)) : undefined;
  let path: PathKind = 'unknown';
  if (local) {
    if (local.candidateType === 'relay') {
      const proto = String(local.relayProtocol ?? '').toLowerCase();
      path = proto === 'tls' ? 'relay-tls' : proto === 'tcp' ? 'relay-tcp' : 'relay-udp';
    } else {
      path = 'direct';
    }
  }

  const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
  const rttSec = num(pair?.currentRoundTripTime) ?? num(remoteIn?.roundTripTime);
  const jitterSec = num(inbound?.jitter);
  return {
    at: performance.now(),
    received: num(inbound?.packetsReceived) ?? 0,
    sent: num(outbound?.packetsSent) ?? 0,
    lost: Math.max(num(inbound?.packetsLost) ?? 0, 0),
    jitterMs: jitterSec === null ? null : Math.round(jitterSec * 1000),
    rttMs: rttSec === null ? null : Math.round(rttSec * 1000),
    path,
  };
}
